import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { toast } from 'sonner';
import { AppConfig, socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
// Importamos globalTimer sólo por efecto de módulo: instancia el singleton y
// arranca su listener + heartbeat de maestro aunque nadie lea el export aquí.
import '../utils/globalTimer';
import { getDefaultTeamsForBracket, getDefaultTeamsForLeaderboard } from '../config/defaultTeams';

interface AppContextType {
  config: AppConfig;
  selectedTemplate: string;
  updateConfig: (section: keyof AppConfig, data: any) => void;
  changeTeamTemplate: (templateId: string) => void;
  openWindow: (path: string, title: string) => void;
  refreshConfig: () => void;
}

const defaultConfig: AppConfig = {
  timer: {
    minutes: 2,
    seconds: 0,
    title: 'Tiempo Restante',
    subtitle: 'Neurolimpiadas 2025',
    categories: []
  },
  bracket: {
    title: 'Bracket de Competición',
    teams: getDefaultTeamsForBracket(),
    tournamentType: 'single',
    allowScores: false,
    workingFields: [],
    displayFields: [],
    fields: [],
    phases: [],
    currentPhase: 'phase1',
    activePhaseId: undefined,
    displayMode: 'working',
    treeBracket: undefined,
    bracketMode: 'phases'
  },
  leaderboard: {
    title: 'Clasificación Neuroteide 2025',
    maxVisiblePositions: 10,
    autoFit: false,
    phaseNames: {
      phase1: 'Relevo',
      phase2: 'Aro Canario',
      phase3: 'Cifras y Letras',
      phase4: 'Figuras de Colores',
      phase5: 'Pizarra'
    },
    teams: []
  },
  carousel: {
    autoPlay: true,
    interval: 5000,
    mode: 'sequential',
    transition: 'fade',
    showControls: true,
    media: []
  },
  podium: {
    title: 'Podium de Ganadores',
    subtitle: 'Neurolimpiadas 2025',
    teams: {
      first: '',
      second: '',
      third: ''
    },
    teamImages: {},
    showPodium: false,
    currentSlide: 0
  },
  pizarra: {
    title: 'Pizarras Neurolimpiadas 2025',
    subtitle: 'Equipos',
    pizarras: [
      { id: '1', teamName: '', number: 0, score: 0, color: '#007bff' },
      { id: '2', teamName: '', number: 0, score: 0, color: '#28a745' },
      { id: '3', teamName: '', number: 0, score: 0, color: '#dc3545' },
      { id: '4', teamName: '', number: 0, score: 0, color: '#ffc107' },
      { id: '5', teamName: '', number: 0, score: 0, color: '#6f42c1' },
      { id: '6', teamName: '', number: 0, score: 0, color: '#e83e8c' },
      { id: '7', teamName: '', number: 0, score: 0, color: '#fd7e14' },
      { id: '8', teamName: '', number: 0, score: 0, color: '#20c997' }
    ],
    showNumbers: true,
    showTeamNames: true,
    showScores: false
  }
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

// Devuelve la config, rellenando equipos desde plantilla SOLO si están vacíos.
// Esto evita sobrescribir las personalizaciones del usuario en cada recarga.
function hydrateConfigWithTemplate(savedConfig: AppConfig, template: string): AppConfig {
  const bracketTeamsEmpty = !savedConfig.bracket.teams || savedConfig.bracket.teams.length === 0;
  const leaderboardTeamsEmpty = !savedConfig.leaderboard.teams || savedConfig.leaderboard.teams.length === 0;

  return {
    ...savedConfig,
    bracket: bracketTeamsEmpty
      ? { ...savedConfig.bracket, teams: getDefaultTeamsForBracket(template) }
      : savedConfig.bracket,
    leaderboard: leaderboardTeamsEmpty
      ? { ...savedConfig.leaderboard, teams: getDefaultTeamsForLeaderboard(savedConfig.leaderboard.phaseNames, template) }
      : savedConfig.leaderboard
  };
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('tenerife-norte');

  useEffect(() => {
    // connect() es idempotente: re-asegura listener + status. Lo llamamos en
    // cada montaje (StrictMode puede montar/desmontar/volver a montar).
    socketService.connect();

    const savedTemplate = localStorageManager.getSelectedTeamTemplate();
    setSelectedTemplate(savedTemplate);

    const savedConfig = localStorageManager.getConfig();
    setConfig(hydrateConfigWithTemplate(savedConfig, savedTemplate));

    // Escuchar cambios de config emitidos por otras ventanas
    const handleConfigUpdate = (payload: any) => {
      if (payload && typeof payload === 'object' && 'section' in payload && 'data' in payload) {
        setConfig(prev => ({ ...prev, [payload.section]: payload.data }));
      }
    };
    socketService.on('configUpdate', handleConfigUpdate);

    // IMPORTANTE: no llamamos a socketService.disconnect() ni a
    // globalTimerService.destroy() en el cleanup. Son singletons de módulo que
    // sobreviven a montajes de AppProvider (StrictMode los remonta, HMR también).
    // Lo único específico de este provider es su propio handler.
    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
    };
  }, []);

  const updateConfig = useCallback((section: keyof AppConfig, data: any) => {
    setConfig(prev => {
      const newSection = { ...(prev[section] as any), ...data };
      const newConfig = { ...prev, [section]: newSection };
      try {
        localStorageManager.updateConfigSection(section, data);
      } catch (error) {
        toast.error(
          `No se pudo guardar la configuración de "${String(section)}" ` +
          `(${error instanceof Error ? error.message : 'error desconocido'}). ` +
          `Cambios visibles en esta ventana, pero no persistidos.`
        );
      }
      socketService.emit('configUpdate', { section, data: newSection });
      return newConfig;
    });
  }, []);

  const changeTeamTemplate = useCallback((templateId: string) => {
    localStorageManager.setSelectedTeamTemplate(templateId);
    setSelectedTemplate(templateId);

    setConfig(prev => {
      const newBracketTeams = getDefaultTeamsForBracket(templateId);
      const newLeaderboardTeams = getDefaultTeamsForLeaderboard(prev.leaderboard.phaseNames, templateId);
      const updatedConfig: AppConfig = {
        ...prev,
        bracket: { ...prev.bracket, teams: newBracketTeams },
        leaderboard: { ...prev.leaderboard, teams: newLeaderboardTeams }
      };
      localStorageManager.setConfig(updatedConfig);
      socketService.emit('configUpdate', { section: 'bracket', data: updatedConfig.bracket });
      socketService.emit('configUpdate', { section: 'leaderboard', data: updatedConfig.leaderboard });
      socketService.emit('templateChange', { templateId });
      return updatedConfig;
    });
  }, []);

  const openWindow = useCallback((path: string, title: string) => {
    const baseUrl = window.location.origin;
    const newWindow = window.open(
      `${baseUrl}${path}`,
      title,
      'width=1920,height=1080,scrollbars=yes,resizable=yes'
    );
    if (newWindow) {
      newWindow.focus();
    } else {
      alert('Por favor, permite las ventanas emergentes para abrir las pantallas.');
    }
  }, []);

  const refreshConfig = useCallback(() => {
    const savedTemplate = localStorageManager.getSelectedTeamTemplate();
    setSelectedTemplate(savedTemplate);
    const savedConfig = localStorageManager.getConfig();
    setConfig(hydrateConfigWithTemplate(savedConfig, savedTemplate));
  }, []);

  const value = useMemo<AppContextType>(
    () => ({ config, selectedTemplate, updateConfig, changeTeamTemplate, openWindow, refreshConfig }),
    [config, selectedTemplate, updateConfig, changeTeamTemplate, openWindow, refreshConfig]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
